import type { ReactNode } from 'react'

import {
  ModalDialog,
  type ModalDialogRole,
  type ModalDialogTone,
} from './ModalDialog'

/**
 * The action weights `controls.css` draws. `danger` is the destructive
 * confirm — bordered and tinted, never filled — and the only spelling a
 * `tone="danger"` dialog's irreversible act has; the discard family keeps
 * `primary` for the safe act and `quiet` for the discard.
 */
type DialogButtonVariant = 'default' | 'primary' | 'quiet' | 'danger'

export interface ConfirmationDialogProps {
  role?: ModalDialogRole
  tone?: ModalDialogTone
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  cancelLabel: ReactNode
  confirmLabel: ReactNode
  onCancel: () => void
  onConfirm: () => void
  cancelDisabled?: boolean
  confirmDisabled?: boolean
  confirmOnEnter?: boolean
  cancelFirst?: boolean
  cancelVariant?: DialogButtonVariant
  confirmVariant?: DialogButtonVariant
  dismissOnBackdrop?: boolean
}

/** A binary question with one explicit cancellation and one confirmation. */
export function ConfirmationDialog({
  role = 'alertdialog',
  tone = 'neutral',
  title,
  description,
  children,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  cancelDisabled = false,
  confirmDisabled = false,
  confirmOnEnter = false,
  cancelFirst = false,
  cancelVariant = 'default',
  confirmVariant = 'primary',
  dismissOnBackdrop = false,
}: ConfirmationDialogProps) {
  const cancel = (
    <button
      type="button"
      className={buttonClass(cancelVariant)}
      disabled={cancelDisabled}
      onClick={onCancel}
    >
      {cancelLabel}
    </button>
  )
  const confirm = (
    <button
      type="button"
      className={buttonClass(confirmVariant)}
      disabled={confirmDisabled}
      onClick={onConfirm}
    >
      {confirmLabel}
    </button>
  )

  return (
    <ModalDialog
      role={role}
      tone={tone}
      title={title}
      description={description}
      dismissOnBackdrop={dismissOnBackdrop}
      {...(cancelDisabled ? {} : { onDismiss: onCancel })}
      {...(confirmDisabled || !confirmOnEnter ? {} : { onConfirm })}
      actions={
        <>
          {cancelFirst ? cancel : confirm}
          {cancelFirst ? confirm : cancel}
        </>
      }
    >
      {children}
    </ModalDialog>
  )
}

function buttonClass(variant: DialogButtonVariant): string {
  return variant === 'default' ? 'button' : `button button--${variant}`
}
